/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable no-nested-ternary */

/* eslint-disable complexity */
import _, { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Divider } from 'src/@vymo/ui/atoms';
import Button from 'src/@vymo/ui/atoms/button';
import Input from 'src/@vymo/ui/atoms/input';
import DotsLoader from 'src/@vymo/ui/atoms/loader/indicator/dots';
import Text from 'src/@vymo/ui/atoms/text';
import { List } from 'src/@vymo/ui/blocks';
import { ReactComponent as NoSearchIcon } from 'src/assets/icons/noSearch.svg';
import { ReactComponent as NoSearchResultsIcon } from 'src/assets/icons/noSearchResults.svg';
import { ReactComponent as SearchIcon } from 'src/assets/icons/search.svg';
import { Keys, locale } from 'src/i18n';
import { getItem } from 'src/indexDb/db';
import { ACTIVITY } from 'src/models/lmsConfig/constants';
import { getRevyreWorkflowConfig } from 'src/models/lmsConfig/queries';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  buildParams,
  finalSelectedTags,
  getModulesAndTypeForSearch,
} from '../queries';
import {
  getSearchLoading,
  getSearchResults,
  getSelectedModule,
} from '../selector';
import { setSelectedModule } from '../slice';
import { fetchSearchResults } from '../thunk';
import { handleSearchNavigation } from './queries';
import SearchDetails from './searchDetails';
import { SearchBarProps, SearchItem } from './types';
import styles from './index.module.scss';

function SearchBar({ minCharsRequired }: SearchBarProps) {
  const popupRef = useRef<any>(null);
  const [value, setValue] = useState('');
  const [showFullList, setShowFullList] = useState(false);
  const dispatch = useAppDispatch();
  const searchResults = useAppSelector((state) =>
    getSearchResults(state),
  ) as SearchItem[];
  const isLoading = useAppSelector(getSearchLoading);
  const selectedModule =
    useAppSelector((state) => getSelectedModule(state)) || 'all';
  const revyreModules = getRevyreWorkflowConfig().filter(
    (module) => module?.type !== 'HIERARCHY',
  );
  const { type, modules } = useMemo(() => getModulesAndTypeForSearch(), []);
  const effectiveModules =
    revyreModules.length > 0
      ? modules.filter(
          (module) => module.display !== false && module.code !== ACTIVITY,
        )
      : modules;
  const [history, setHistory] = useState<any>([]);
  const [inputWidth, setInputWidth] = useState<number>(0);
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);

  useEffect(() => {
    if (popupRef.current) {
      const { width } = popupRef.current.getBoundingClientRect();
      setInputWidth(width + 40);
    }
  }, []);

  useEffect(() => {
    getItem('SEARCH_HISTORY').then((res) => {
      setHistory(res || []);
    });
  }, []);

  const triggerSearch = debounce(
    (query: string, full: boolean, moduleCode: string) => {
      const params = buildParams(moduleCode, full);
      dispatch(fetchSearchResults({ query, params, minCharsRequired, type }));
    },
    200,
  );

  const onGetAutoCompletions = (text: string) => {
    if (text.length >= minCharsRequired && showFullList) {
      triggerSearch(text, true, selectedModule);
    } else {
      dispatch(setSelectedModule('all'));
      triggerSearch(text, false, 'all');
      setShowFullList(false);
    }
  };

  const debounceSearch = (moduleCodes, query) => {
    if (query.length >= minCharsRequired) {
      triggerSearch(query, true, moduleCodes);
    }
  };

  const onModuleSelection = (checked) => {
    const selectedTags = finalSelectedTags(checked, selectedModule);
    dispatch(setSelectedModule(selectedTags));
    debounceSearch(selectedTags, value);
  };

  const hasTyped = value.length > 0;
  const hasSearchResults = searchResults.length > 0;
  const hasHistory = history?.length > 0;

  return (
    <div
      data-test-id="search-popup"
      onFocus={() => setShowSearchModal(true)}
      onBlur={() => setShowSearchModal(false)}
      className={styles.searchPopupWrapper}
    >
      <Input
        ref={popupRef}
        placeholder={locale(Keys.SEARCH_PLACEHOLDER)}
        value={value}
        onChange={(e) => {
          onGetAutoCompletions(e);
          setValue(e);
        }}
        iconLeft={<SearchIcon />}
        classNames={styles.search}
        data-test-id="header-search"
      />
      {showSearchModal && (
        <div
          className={styles.search__dropdown}
          style={{ width: inputWidth }}
          onMouseDown={(e) => e.preventDefault()}
          data-test-id="search-popup-content"
        >
          {value.length < minCharsRequired ? (
            <>
              {hasTyped && (
                <>
                  <Text
                    type="subText"
                    variant="info"
                    classNames={styles.search__minCharMessage}
                  >
                    {locale(Keys.MIN_CHARS_TO_SEE_RESULTS)}
                  </Text>
                  <Divider />
                </>
              )}

              {hasHistory && (
                <>
                  <Text
                    classNames={styles.search__result__title}
                    type="subText"
                  >
                    {locale(Keys.RECENT_SEARCHES)}
                  </Text>
                  <List
                    className={styles.search__result}
                    onItemClick={(idx) =>
                      handleSearchNavigation(history[idx], type, dispatch)
                    }
                  >
                    {history?.map((item) => (
                      <List.Item
                        key={item.code}
                        className={styles.search__result__item}
                      >
                        <Avatar
                          size="small"
                          background="var(--background-default)"
                          text={_.capitalize(item.name?.charAt(0))}
                        />

                        <div className={styles.search__result__item__title}>
                          {item.name}
                        </div>
                      </List.Item>
                    ))}
                  </List>
                </>
              )}

              {!hasHistory && (
                <div className={styles.search__dropdown__emptyState}>
                  <div className={styles.search__dropdown__emptyState__text}>
                    <NoSearchIcon />
                    <Text>{locale(Keys.RESULT_WILL_POPULATE_HERE)}</Text>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {isLoading && (
                <div className={styles.search__dropdown__emptyState}>
                  <div className={styles.search__dropdown__emptyState__text}>
                    <DotsLoader color="var(--icon-default)" />
                    <Text>Loading</Text>
                    <Text type="subText">
                      {locale(Keys.PLEASE_WAIT_WHILE_WE_COMPLETE_YOUR_QUERY)}
                    </Text>
                  </div>
                </div>
              )}
              {!isLoading && (
                <>
                  {effectiveModules?.length > 0 && (
                    <div className={styles.search__tags}>
                      {[{ code: 'all', name: 'All' }, ...effectiveModules]
                        .filter((module) => Boolean(module.name))
                        .map((module) => (
                          <Button
                            type="text"
                            size="small"
                            variant={
                              selectedModule.split(',').includes(module.code)
                                ? 'subtle'
                                : 'default'
                            }
                            className={styles.search__tags__tag}
                            onClick={() => onModuleSelection(module)}
                          >
                            {module.name}
                          </Button>
                        ))}
                    </div>
                  )}

                  {hasSearchResults && (
                    <>
                      <Text
                        classNames={styles.search__result__title}
                        type="subText"
                      >
                        {locale(Keys.SEARCH_RESULTS)}
                      </Text>
                      <SearchDetails items={searchResults} type={type} />
                    </>
                  )}

                  {!hasSearchResults && !isLoading && (
                    <div className={styles.search__dropdown__emptyState}>
                      <div
                        className={styles.search__dropdown__emptyState__text}
                      >
                        <NoSearchResultsIcon />
                        <Text>{locale(Keys.NO_MATCH_FOUND)}</Text>
                        <Text type="subText">
                          {locale(Keys.TRY_DIFFERENT_KEYWORD)}
                        </Text>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
